import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Trash2, 
  Edit3, 
  Users, 
  Clock,
  Layout as LayoutIcon,
  Lock,
  Globe,
  Palette,
  FileText,
  Workflow,
  GitBranch,
  MessageSquare,
  Lightbulb,
  Target,
  Kanban,
  ChevronRight
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

// Board background colors
const BOARD_COLORS = [
  { id: 'white', name: 'White', color: '#FFFFFF', textColor: '#374151' },
  { id: 'light-gray', name: 'Light Gray', color: '#F3F4F6', textColor: '#374151' },
  { id: 'warm', name: 'Warm White', color: '#FFFBEB', textColor: '#92400E' },
  { id: 'cool', name: 'Cool Blue', color: '#EFF6FF', textColor: '#1E40AF' },
  { id: 'mint', name: 'Mint', color: '#ECFDF5', textColor: '#065F46' },
  { id: 'lavender', name: 'Lavender', color: '#F5F3FF', textColor: '#5B21B6' },
  { id: 'dark', name: 'Dark', color: '#1F2937', textColor: '#F9FAFB' },
  { id: 'slate', name: 'Slate', color: '#0F172A', textColor: '#E2E8F0' },
];

// Board use templates
const BOARD_TEMPLATES = [
  { id: 'blank', name: 'Blank Canvas', icon: FileText, description: 'Start from scratch' },
  { id: 'brainstorm', name: 'Brainstorming', icon: Lightbulb, description: 'Mind mapping & ideas' },
  { id: 'flowchart', name: 'Flowchart', icon: GitBranch, description: 'Process diagrams' },
  { id: 'workflow', name: 'Workflow', icon: Workflow, description: 'Automation flows' },
  { id: 'retrospective', name: 'Retrospective', icon: MessageSquare, description: 'Team feedback' },
  { id: 'planning', name: 'Sprint Planning', icon: Target, description: 'Project planning' },
  { id: 'kanban', name: 'Kanban Board', icon: Kanban, description: 'Task management' },
];

// Default tile colors
const TILE_COLORS = [
  { color: '#22C55E', name: 'Green' },
  { color: '#3B82F6', name: 'Blue' },
  { color: '#F59E0B', name: 'Amber' },
  { color: '#8B5CF6', name: 'Purple' },
  { color: '#EF4444', name: 'Red' },
  { color: '#EC4899', name: 'Pink' },
  { color: '#06B6D4', name: 'Cyan' },
  { color: '#FFFFFF', name: 'White' }
];

// Default arrow colors
const ARROW_COLORS = [
  { color: '#000000', name: 'Black' },
  { color: '#6B7280', name: 'Gray' },
  { color: '#3B82F6', name: 'Blue' },
  { color: '#22C55E', name: 'Green' },
  { color: '#EF4444', name: 'Red' },
  { color: '#F59E0B', name: 'Amber' }
];

export default function CanvasList() {
  const navigate = useNavigate();
  const [canvases, setCanvases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCanvas, setSelectedCanvas] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '',
    boardColor: 'white',
    template: 'blank',
    visibility: 'private',
    defaultTileColor: '#3B82F6',
    defaultArrowColor: '#000000'
  });
  const [creating, setCreating] = useState(false);
  const [createStep, setCreateStep] = useState(1);

  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      boardColor: 'white',
      template: 'blank',
      visibility: 'private',
      defaultTileColor: '#3B82F6',
      defaultArrowColor: '#000000'
    });
    setCreateStep(1);
  };

  const fetchCanvases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/canvas`);
      setCanvases(response.data.canvases || []);
    } catch (error) {
      console.error('Error fetching canvases:', error);
      toast.error('Failed to load canvases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCanvases();
  }, [fetchCanvases]);

  const handleCreateCanvas = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a canvas name');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/api/canvas`, {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        settings: {
          boardColor: formData.boardColor,
          template: formData.template,
          visibility: formData.visibility,
          defaultTileColor: formData.defaultTileColor,
          defaultArrowColor: formData.defaultArrowColor
        }
      });
      
      toast.success('Canvas created!');
      setShowCreateDialog(false);
      resetFormData();
      
      // Navigate to the new canvas
      navigate(`/canvas/${response.data.id}`);
    } catch (error) {
      console.error('Error creating canvas:', error);
      toast.error('Failed to create canvas');
    } finally {
      setCreating(false);
    }
  };

  const handleRenameCanvas = async () => {
    if (!formData.name.trim() || !selectedCanvas) return;

    try {
      await axios.patch(`${API}/api/canvas/${selectedCanvas.id}`, {
        name: formData.name.trim(),
        description: formData.description.trim() || null
      });
      
      toast.success('Canvas renamed');
      setShowRenameDialog(false);
      setSelectedCanvas(null);
      setFormData({ name: '', description: '' });
      fetchCanvases();
    } catch (error) {
      console.error('Error renaming canvas:', error);
      toast.error('Failed to rename canvas');
    }
  };

  const handleDeleteCanvas = async () => {
    if (!selectedCanvas) return;

    try {
      await axios.delete(`${API}/api/canvas/${selectedCanvas.id}`);
      toast.success('Canvas deleted');
      setShowDeleteDialog(false);
      setSelectedCanvas(null);
      fetchCanvases();
    } catch (error) {
      console.error('Error deleting canvas:', error);
      toast.error('Failed to delete canvas');
    }
  };

  const openRenameDialog = (canvas, e) => {
    e.stopPropagation();
    setSelectedCanvas(canvas);
    setFormData({ name: canvas.name, description: canvas.description || '' });
    setShowRenameDialog(true);
  };

  const openDeleteDialog = (canvas, e) => {
    e.stopPropagation();
    setSelectedCanvas(canvas);
    setShowDeleteDialog(true);
  };

  const filteredCanvases = canvases.filter(canvas =>
    canvas.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (canvas.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const badges = {
      owner: { label: 'Owner', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      editor: { label: 'Editor', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      viewer: { label: 'View Only', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' }
    };
    return badges[role] || badges.viewer;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background" data-testid="canvas-list-page">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Canvas</h1>
              <p className="text-muted-foreground mt-1">
                Create and collaborate on visual boards
              </p>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="gap-2"
              data-testid="create-canvas-btn"
            >
              <Plus className="h-4 w-4" />
              New Canvas
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search canvases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="canvas-search-input"
            />
          </div>

        {/* Canvas Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-muted" />
                <CardContent className="p-4">
                  <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCanvases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <LayoutIcon className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {searchQuery ? 'No canvases found' : 'No canvases yet'}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Create your first canvas to start brainstorming and collaborating with your team.'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Canvas
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCanvases.map((canvas) => {
              const roleBadge = getRoleBadge(canvas.user_role);
              return (
                <Card
                  key={canvas.id}
                  className="group cursor-pointer hover:border-primary/50 transition-all duration-200 overflow-hidden"
                  onClick={() => navigate(`/canvas/${canvas.id}`)}
                  data-testid={`canvas-card-${canvas.id}`}
                >
                  {/* Thumbnail / Preview */}
                  <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 relative flex items-center justify-center">
                    <LayoutIcon className="h-12 w-12 text-muted-foreground/30" />
                    {canvas.element_count > 0 && (
                      <div className="absolute bottom-2 right-2 px-2 py-1 bg-background/80 backdrop-blur-sm rounded text-xs text-muted-foreground">
                        {canvas.element_count} element{canvas.element_count !== 1 ? 's' : ''}
                      </div>
                    )}
                    {/* Actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="secondary" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => openRenameDialog(canvas, e)}>
                            <Edit3 className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                            <Users className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                          {canvas.user_role === 'owner' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => openDeleteDialog(canvas, e)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">
                          {canvas.name}
                        </h3>
                        {canvas.description && (
                          <p className="text-sm text-muted-foreground truncate mt-0.5">
                            {canvas.description}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${roleBadge.className}`}>
                        {roleBadge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(canvas.updated_at), { addSuffix: true })}
                      </div>
                      {canvas.permissions?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {canvas.permissions.length + 1}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        </div>
      </div>

      {/* Create Canvas Dialog - Enhanced Setup */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetFormData();
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create New Canvas</DialogTitle>
            <DialogDescription>
              {createStep === 1 ? 'Choose a template and customize your board' : 'Set up colors and sharing preferences'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 py-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${createStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>1</div>
            <div className={`w-12 h-0.5 ${createStep >= 2 ? 'bg-primary' : 'bg-muted'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${createStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>2</div>
          </div>

          {createStep === 1 ? (
            <div className="space-y-6 py-4">
              {/* Name & Description */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Canvas Name</Label>
                  <Input
                    placeholder="e.g., Q1 Planning Session"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="canvas-name-input"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Description (optional)</Label>
                  <Input
                    placeholder="Brief description of this canvas..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>

              {/* Board Template */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Board Template</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {BOARD_TEMPLATES.map((template) => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={template.id}
                        onClick={() => setFormData(prev => ({ ...prev, template: template.id }))}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:border-primary/50 ${
                          formData.template === template.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-muted'
                        }`}
                      >
                        <Icon className={`h-6 w-6 mb-2 ${formData.template === template.id ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="text-xs font-medium text-center">{template.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Board Color */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Board Background</Label>
                <div className="flex flex-wrap gap-2">
                  {BOARD_COLORS.map((bg) => (
                    <button
                      key={bg.id}
                      onClick={() => setFormData(prev => ({ ...prev, boardColor: bg.id }))}
                      title={bg.name}
                      className={`w-10 h-10 rounded-lg border-2 transition-all flex items-center justify-center ${
                        formData.boardColor === bg.id 
                          ? 'border-primary scale-110 ring-2 ring-primary/20' 
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      style={{ 
                        backgroundColor: bg.color,
                        border: bg.color === '#FFFFFF' ? '1px solid #E5E7EB' : undefined 
                      }}
                    >
                      {formData.boardColor === bg.id && (
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: bg.textColor }} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Visibility / Shared With */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Visibility</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, visibility: 'private' }))}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      formData.visibility === 'private' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted hover:border-primary/30'
                    }`}
                  >
                    <Lock className={`h-5 w-5 ${formData.visibility === 'private' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="text-left">
                      <p className="font-medium text-sm">Private</p>
                      <p className="text-xs text-muted-foreground">Only you can access</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, visibility: 'team' }))}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      formData.visibility === 'team' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted hover:border-primary/30'
                    }`}
                  >
                    <Users className={`h-5 w-5 ${formData.visibility === 'team' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="text-left">
                      <p className="font-medium text-sm">Team</p>
                      <p className="text-xs text-muted-foreground">Share with team members</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Default Colors */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Default Colors</Label>
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  {/* Tile Color */}
                  <div className="space-y-2">
                    <span className="text-xs text-muted-foreground">Tile Color</span>
                    <div className="flex flex-wrap gap-1.5">
                      {TILE_COLORS.map(({ color, name }) => (
                        <button
                          key={color}
                          title={name}
                          className={`w-7 h-7 rounded border-2 transition-all ${
                            formData.defaultTileColor === color 
                              ? 'border-primary scale-110' 
                              : 'border-transparent hover:border-gray-400'
                          }`}
                          style={{ 
                            backgroundColor: color,
                            border: color === '#FFFFFF' ? '1px solid #D1D5DB' : undefined
                          }}
                          onClick={() => setFormData(prev => ({ ...prev, defaultTileColor: color }))}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Arrow Color */}
                  <div className="space-y-2">
                    <span className="text-xs text-muted-foreground">Arrow Color</span>
                    <div className="flex flex-wrap gap-1.5">
                      {ARROW_COLORS.map(({ color, name }) => (
                        <button
                          key={color}
                          title={name}
                          className={`w-7 h-7 rounded-full border-2 transition-all ${
                            formData.defaultArrowColor === color 
                              ? 'border-primary scale-110' 
                              : 'border-transparent hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => setFormData(prev => ({ ...prev, defaultArrowColor: color }))}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Preview</Label>
                <div 
                  className="h-24 rounded-lg border flex items-center justify-center"
                  style={{ backgroundColor: BOARD_COLORS.find(b => b.id === formData.boardColor)?.color }}
                >
                  <div 
                    className="px-4 py-2 rounded text-sm font-medium"
                    style={{ 
                      backgroundColor: formData.defaultTileColor,
                      color: formData.defaultTileColor === '#FFFFFF' || formData.defaultTileColor === '#F59E0B' ? '#374151' : '#FFFFFF'
                    }}
                  >
                    {formData.name || 'Sample Tile'}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {createStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => setCreateStep(2)}
                  disabled={!formData.name.trim()}
                  className="gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCreateStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={handleCreateCanvas} 
                  disabled={creating || !formData.name.trim()}
                  data-testid="create-canvas-submit"
                >
                  {creating ? 'Creating...' : 'Create Canvas'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Canvas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameCanvas} disabled={!formData.name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Canvas</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCanvas?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCanvas}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
